import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { ProfileCardSkeleton } from '../components/LoadingSkeleton';

const MatrimonyPage = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { error } = useToast();

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [gender, setGender] = useState('All');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [city, setCity] = useState('');
  const [education, setEducation] = useState('');

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/matrimony/profiles');
        setProfiles(data);
      } catch (err) {
        console.error('Failed to fetch profiles', err);
        error('Failed to load profiles. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
  }, [error]);

  const filteredProfiles = useMemo(() => {
    return profiles.filter(profile => {
      const matchesSearch = searchQuery.trim() === '' || 
        profile.fullName?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesGender = gender === 'All' || profile.gender === gender;
      
      const matchesAge = (!ageMin || profile.age >= parseInt(ageMin)) &&
        (!ageMax || profile.age <= parseInt(ageMax));
      
      const matchesCity = !city.trim() || 
        profile.currentResidence?.toLowerCase().includes(city.toLowerCase()) ||
        profile.city?.toLowerCase().includes(city.toLowerCase());
      
      const matchesEducation = !education.trim() ||
        profile.education?.toLowerCase().includes(education.toLowerCase());

      return matchesSearch && matchesGender && matchesAge && matchesCity && matchesEducation;
    });
  }, [profiles, searchQuery, gender, ageMin, ageMax, city, education]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setGender('All');
    setAgeMin('');
    setAgeMax('');
    setCity('');
    setEducation('');
  };

  if (!currentUser) {
    return (
      <div className="auth-required">
        <div className="container">
          <div className="auth-required-content">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <h2>Members Only</h2>
            <p>Please log in to browse matrimony profiles and connect with potential matches.</p>
            <div className="auth-required-actions">
              <button onClick={() => navigate('/login')} className="btn btn-primary btn-lg">
                Log In
              </button>
              <button onClick={() => navigate('/register')} className="btn btn-outline btn-lg">
                Create Account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="matrimony-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Find Your Perfect Match</h1>
            <p>Browse verified profiles from our trusted community</p>
            <Link to="/create-profile" className="btn btn-primary btn-lg">
              Create Your Profile
            </Link>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="matrimony-layout">
          {/* Filters Sidebar */}
          <aside className="filters-sidebar">
            <div className="filters-header">
              <h3>Filters</h3>
              <button onClick={handleClearFilters} className="btn-text">
                Clear All
              </button>
            </div>

            <div className="filter-group">
              <label className="filter-label">Search by Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">Gender</label>
              <select
                className="form-select"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="All">All</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Age Range</label>
              <div className="age-inputs">
                <input
                  type="number"
                  className="form-input"
                  placeholder="Min"
                  value={ageMin}
                  onChange={(e) => setAgeMin(e.target.value)}
                />
                <span>to</span>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Max"
                  value={ageMax}
                  onChange={(e) => setAgeMax(e.target.value)}
                />
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">City</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Pune"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">Education</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Engineering"
                value={education}
                onChange={(e) => setEducation(e.target.value)}
              />
            </div>
          </aside>

          {/* Profiles Grid */}
          <main className="profiles-content">
            <div className="results-header">
              <h2>
                {filteredProfiles.length} {filteredProfiles.length === 1 ? 'Profile' : 'Profiles'} Found
              </h2>
            </div>

            {loading ? (
              <div className="profiles-grid">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <ProfileCardSkeleton key={i} />
                ))}
              </div>
            ) : filteredProfiles.length > 0 ? (
              <div className="profiles-grid">
                {filteredProfiles.map((profile) => (
                  <Link
                    key={profile.id}
                    to={`/matrimony/${profile.id}`}
                    className="profile-card"
                  >
                    <div className="profile-image">
                      <img
                        src={profile.profilePhotoUrl || 'https://placehold.co/400x400/E3F2FD/2E5C8A?text=Profile'}
                        alt={profile.fullName}
                      />
                      <div className="verified-badge">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      </div>
                    </div>
                    <div className="profile-details">
                      <h3>{profile.fullName}</h3>
                      <div className="profile-meta">
                        <span>{profile.age} yrs</span>
                        <span>•</span>
                        <span>{profile.gender}</span>
                      </div>
                      <div className="profile-location">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        {profile.currentResidence || profile.city || 'Location not specified'}
                      </div>
                      <div className="profile-education">
                        {profile.education || 'Education not specified'}
                      </div>
                      <div className="profile-occupation">
                        {profile.occupation || 'Occupation not specified'}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                <h3>No profiles found</h3>
                <p>Try adjusting your filters to see more results</p>
                <button onClick={handleClearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      <style jsx>{`
        .matrimony-page {
          min-height: 100vh;
          padding-bottom: var(--space-16);
        }

        .page-hero {
          background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-500) 100%);
          padding: var(--space-16) 0;
          color: white;
          text-align: center;
        }

        .hero-content h1 {
          font-size: var(--text-5xl);
          color: white;
          margin-bottom: var(--space-4);
        }

        .hero-content p {
          font-size: var(--text-lg);
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: var(--space-8);
        }

        .matrimony-layout {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: var(--space-8);
          margin-top: calc(var(--space-12) * -1);
          position: relative;
        }

        .filters-sidebar {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-6);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--neutral-200);
          height: fit-content;
          position: sticky;
          top: var(--space-20);
        }

        .filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-6);
          padding-bottom: var(--space-4);
          border-bottom: 1px solid var(--neutral-200);
        }

        .filters-header h3 {
          font-size: var(--text-xl);
          margin: 0;
        }

        .btn-text {
          background: none;
          border: none;
          color: var(--primary-500);
          font-size: var(--text-sm);
          font-weight: var(--weight-medium);
          cursor: pointer;
          padding: 0;
        }

        .btn-text:hover {
          color: var(--primary-700);
        }

        .filter-group {
          margin-bottom: var(--space-6);
        }

        .filter-label {
          display: block;
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          color: var(--neutral-700);
          margin-bottom: var(--space-2);
        }

        .age-inputs {
          display: flex;
          align-items: center;
          gap: var(--space-2);
        }

        .age-inputs span {
          color: var(--neutral-500);
          font-size: var(--text-sm);
        }

        .profiles-content {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-8);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--neutral-200);
        }

        .results-header {
          margin-bottom: var(--space-8);
        }

        .results-header h2 {
          font-size: var(--text-2xl);
          color: var(--neutral-900);
        }

        .profiles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: var(--space-6);
        }

        .profile-card {
          background: white;
          border: 1px solid var(--neutral-200);
          border-radius: var(--radius-xl);
          overflow: hidden;
          text-decoration: none;
          transition: all var(--transition-base);
          cursor: pointer;
        }

        .profile-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-md);
          border-color: var(--primary-300);
        }

        .profile-image {
          position: relative;
          aspect-ratio: 1;
          overflow: hidden;
        }

        .profile-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .verified-badge {
          position: absolute;
          top: var(--space-3);
          right: var(--space-3);
          width: 28px;
          height: 28px;
          background: var(--success);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .profile-details {
          padding: var(--space-4);
        }

        .profile-details h3 {
          font-size: var(--text-lg);
          font-weight: var(--weight-bold);
          color: var(--neutral-900);
          margin-bottom: var(--space-2);
        }

        .profile-meta {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-size: var(--text-sm);
          color: var(--neutral-600);
          margin-bottom: var(--space-3);
        }

        .profile-location {
          display: flex;
          align-items: center;
          gap: var(--space-1);
          font-size: var(--text-sm);
          color: var(--neutral-600);
          margin-bottom: var(--space-2);
        }

        .profile-education,
        .profile-occupation {
          font-size: var(--text-sm);
          color: var(--neutral-700);
          margin-bottom: var(--space-1);
        }

        .empty-state {
          text-align: center;
          padding: var(--space-16);
          color: var(--neutral-600);
        }

        .empty-state svg {
          color: var(--neutral-400);
          margin-bottom: var(--space-4);
        }

        .empty-state h3 {
          font-size: var(--text-2xl);
          color: var(--neutral-900);
          margin-bottom: var(--space-2);
        }

        .empty-state p {
          margin-bottom: var(--space-6);
        }

        .auth-required {
          min-height: 80vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .auth-required-content {
          text-align: center;
          max-width: 500px;
        }

        .auth-required-content svg {
          color: var(--primary-500);
          margin-bottom: var(--space-6);
        }

        .auth-required-content h2 {
          font-size: var(--text-4xl);
          margin-bottom: var(--space-4);
        }

        .auth-required-content p {
          color: var(--neutral-600);
          margin-bottom: var(--space-8);
        }

        .auth-required-actions {
          display: flex;
          gap: var(--space-4);
          justify-content: center;
        }

        @media (max-width: 768px) {
          .matrimony-layout {
            grid-template-columns: 1fr;
          }

          .filters-sidebar {
            position: static;
          }

          .page-hero h1 {
            font-size: var(--text-4xl);
          }

          .profiles-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default MatrimonyPage;